import { ApplicationConfig, inject, isDevMode, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { provideServiceWorker, SwUpdate, type VersionReadyEvent } from '@angular/service-worker';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { filter } from 'rxjs';

import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';

import { routes } from './app.routes';

import { deviceKeyInterceptor, errorInterceptor, loggingInterceptor } from '@core/interceptors';
import { notificationsReducer } from '@core/state/notifications/notifications.reducers';
import { productContainersReducer } from '@core/state/product-containers/product-containers.reducers';
import { ProductContainersEffects } from '@core/state/product-containers/product-containers.effects';
import { printKiosksReducer } from '@core/state/print-kiosks/print-kiosks.reducers';
import { labelPrintersReducer } from '@core/state/label-printers/label-printers.reducers';
import { LabelPrintersEffects } from '@core/state/label-printers/label-printers.effects';
import { PrintKiosksEffects } from '@core/state/print-kiosks/print-kiosks.effects';
import { productsReducer } from '@core/state/products/products.reducers';
import { ProductsEffects } from '@core/state/products/products.effects';
import { containersReducer } from '@core/state/containers/containers.reducers';
import { ContainersEffects } from '@core/state/containers/containers.effects';
import { jwtInterceptor } from '@auth/jwt.interceptor';
import { lastValueFrom } from 'rxjs';
import { TokenService } from '@auth/token.service';


export const appConfig: ApplicationConfig = {
  providers: [
    //{ provide: 'BASE_URL', useFactory: () => document.getElementsByTagName('base')[0].href, deps: [] },
    provideAppInitializer(() => {
      const tokenService = inject(TokenService);
      return lastValueFrom(tokenService.refreshToken());
    }),
    provideAppInitializer(() => {
      if (isDevMode()) return;
      const swUpdate = inject(SwUpdate);
      swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(async () => {
          await swUpdate.activateUpdate();
          window.location.reload();
        });
    }),
    provideHttpClient(
      withInterceptors([loggingInterceptor, errorInterceptor, deviceKeyInterceptor, jwtInterceptor])),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideStore(),
    provideState('notifications', notificationsReducer),
    provideState('productContainers', productContainersReducer),
    provideState('printKiosks', printKiosksReducer),
    provideState('labelPrinters', labelPrintersReducer),
    provideState('products', productsReducer),
    provideState('containers', containersReducer),
    // All effects are shared by kiosk and admin; feature-specific effects could be lazy-loaded via provideEffects() in feature route providers.
    provideEffects([
      ProductContainersEffects,
      LabelPrintersEffects,
      PrintKiosksEffects,
      ProductsEffects,
      ContainersEffects,
    ]),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ]
};
